import { DirectUpload } from "@rails/activestorage"
import { Turbo } from "@hotwired/turbo-rails"
import { extractPhotoMetadata } from "./image_geolocation"

const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads"

export const DEFAULT_MAX_FILES = 100
export const DEFAULT_MAX_FILE_SIZE = 250 * 1024 * 1024 // 250MB
export const DEFAULT_CONCURRENCY = 3

// Walks the DataTransfer, recursing into directories via the FileSystem Entry
// API so dropped folders contribute every file inside them. Falls back to
// dataTransfer.files when the browser doesn't expose webkitGetAsEntry.
export async function collectFromDataTransfer(dataTransfer) {
  const items = dataTransfer.items
  const supportsEntries =
    items && items.length > 0 && typeof items[0].webkitGetAsEntry === "function"

  if (!supportsEntries) {
    return Array.from(dataTransfer.files).map((file) => ({ file, relativePath: "" }))
  }

  const collected = []
  const roots = []
  for (let i = 0; i < items.length; i++) {
    if (items[i].kind !== "file") continue
    const entry = items[i].webkitGetAsEntry()
    if (entry) roots.push(entry)
  }

  await Promise.all(roots.map((entry) => walkEntry(entry, "", collected)))
  return collected
}

async function walkEntry(entry, parentPath, collected) {
  if (entry.isFile) {
    const file = await fileFromEntry(entry)
    if (file) collected.push({ file, relativePath: parentPath })
    return
  }

  if (entry.isDirectory) {
    const childPath = parentPath ? `${parentPath}/${entry.name}` : entry.name
    const children = await readAllEntries(entry)
    for (const child of children) {
      await walkEntry(child, childPath, collected)
    }
  }
}

function fileFromEntry(entry) {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      () => resolve(null),
    )
  })
}

// The reader API is paged: readEntries returns up to a few entries per call
// and signals "no more" by returning an empty array. Loop until drained.
async function readAllEntries(directoryEntry) {
  const reader = directoryEntry.createReader()
  const results = []
  while (true) {
    const batch = await new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject)
    })
    if (batch.length === 0) break
    results.push(...batch)
  }
  return results
}

// Builds a standalone error row from the shared upload-item template, for a
// message that belongs to the drop as a whole (the file cap) rather than to any
// one file — so there is no filename, size or progress bar to show.
export function buildNoticeRow(templateElement, message) {
  const container = templateElement.content.cloneNode(true).querySelector("[data-upload-item]")

  container.querySelector(".uploading").classList.add("hidden")
  container.querySelector(".error").classList.remove("hidden")
  container.querySelector("[data-upload-progress]")?.remove()
  container.querySelector("[data-filesize]")?.remove()

  const label = container.querySelector("[data-filename]")
  label.textContent = message
  label.classList.remove("truncate")
  label.classList.add("text-red-600", "whitespace-normal")

  return container
}

export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

// Uploads a list of { file, relativePath } entries: DirectUpload of the bytes,
// then a POST per file creating the DocumentFile (the server resolves
// relative_path into nested folders). UI is owned by the caller through the
// callbacks — each receives the element returned by onPreview for that entry.
//
// Uploads run through a small worker pool instead of all at once: a dropped
// folder can easily hold dozens of files, and firing them concurrently both
// hammers the server and widens the window for duplicate-folder races on
// sibling relative_path segments.
export async function uploadEntries(entries, options) {
  const {
    url,
    folderId = null,
    maxFiles = DEFAULT_MAX_FILES,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    concurrency = DEFAULT_CONCURRENCY,
    // Batch mode: each create skips its (recursive) folder-totals refresh; the caller
    // fetches document_folders#totals ONCE after this resolves.
    deferTotals = false,
    onPreview,
    onProgress = () => {},
    onSuccess = () => {},
    onError = () => {},
    onTruncated = () => {},
    fileTooLargeMessage = "",
    uploadFailedMessage = "",
  } = options

  // Dropping a folder is one gesture that can carry hundreds of files, so the
  // cap has to be reported — silently uploading the first N reads as success.
  if (entries.length > maxFiles) {
    onTruncated(entries.length - maxFiles, maxFiles)
  }

  const queue = []
  entries.slice(0, maxFiles).forEach((entry) => {
    const preview = onPreview(entry.file, entry.relativePath)
    if (entry.file.size > maxFileSize) {
      onError(preview, fileTooLargeMessage)
      return
    }
    queue.push({ ...entry, preview })
  })

  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const item = queue.shift()
      await uploadOne(item, {
        url,
        folderId,
        deferTotals,
        onProgress,
        onSuccess,
        onError,
        uploadFailedMessage,
      })
    }
  })

  await Promise.all(workers)
}

// Fetches the batched folder-totals refresh and applies its turbo streams — the
// counterpart of uploadEntries' deferTotals option.
export async function refreshFolderTotals(totalsUrl) {
  if (!totalsUrl) return

  try {
    const response = await fetch(totalsUrl, { headers: { Accept: "text/vnd.turbo-stream.html" } })
    if (response.ok) Turbo.renderStreamMessage(await response.text())
  } catch (_e) {
    // Totals are cosmetic — a failed refresh must not fail the finished upload batch.
  }
}

async function uploadOne({ file, relativePath, preview }, options) {
  const { url, folderId, deferTotals, onProgress, onSuccess, onError, uploadFailedMessage } =
    options

  let blob
  try {
    blob = await directUpload(file, preview, onProgress)
  } catch (error) {
    onError(preview, error)
    return
  }

  await submitFile(blob, file, relativePath, preview, {
    url,
    folderId,
    deferTotals,
    onSuccess,
    onError,
    uploadFailedMessage,
  })
}

function directUpload(file, preview, onProgress) {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            onProgress(preview, Math.round((event.loaded / event.total) * 100))
          }
        })
      },
    })

    upload.create((error, blob) => {
      if (error) {
        reject(error)
      } else {
        resolve(blob)
      }
    })
  })
}

async function submitFile(blob, file, relativePath, preview, options) {
  const { url, folderId, deferTotals, onSuccess, onError, uploadFailedMessage } = options
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const formData = new FormData()

  formData.append("document_file[attachment]", blob.signed_id)
  if (folderId) {
    formData.append("document_file[document_folder_id]", folderId)
  }
  if (relativePath) {
    formData.append("document_file[relative_path]", relativePath)
  }
  if (deferTotals) {
    formData.append("document_file[defer_totals]", "1")
  }

  await appendPhotoMetadata(formData, file)

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": token,
        Accept: "text/vnd.turbo-stream.html, application/json",
      },
      body: formData,
    })

    const text = await response.text()
    if (response.ok) {
      Turbo.renderStreamMessage(text)
      onSuccess(preview)
    } else {
      onError(preview, extractErrorMessage(text, uploadFailedMessage))
    }
  } catch (error) {
    onError(preview, error)
  }
}

// For images, read EXIF metadata (location, capture time, camera) in the
// browser and send it alongside the file so the server can persist it on the
// Active Storage blob. Non-images and metadata-less photos send nothing.
async function appendPhotoMetadata(formData, file) {
  if (!file.type || !file.type.startsWith("image/")) return

  try {
    const metadata = await extractPhotoMetadata(file)
    Object.entries(metadata).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return
      formData.append(`document_file[geodata][${key}]`, value)
    })
  } catch (error) {
    console.warn("Photo metadata extraction failed:", error)
  }
}

function extractErrorMessage(body, fallback) {
  try {
    const parsed = JSON.parse(body)
    if (parsed && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
      return parsed.errors.join(", ")
    }
  } catch (_e) {
    // not JSON, fall through
  }
  return fallback
}
